import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shell } from "@/components/Shell";
import { consumeInloglink, type UseInloglinkStatus } from "@/features/session/localAccount";

/**
 * Where an inloglink lands: it consumes the link once and gets out of the way. It never retries
 * by itself - a reload finds the nonce gone, which reads the same as an expired link.
 * See docs/adr/0066-an-inloglink-is-the-fourth-door-and-a-password-becomes-optional.md.
 */
export function InloglinkPage() {
  const { t } = useTranslation(["session", "common"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const parameters = new URLSearchParams(window.location.search);
  const nonce = parameters.get("n") ?? "";
  const token = parameters.get("t") ?? "";

  // A link that carries no nonce is knowable at render, so it is an initial value rather than
  // something an effect has to synchronise.
  const incomplete = !nonce || !token;

  const [status, setStatus] = useState<UseInloglinkStatus | "bezig">(
    incomplete ? "ongeldig" : "bezig",
  );

  // A link is spent by being presented, so this fires once per page even though React invokes
  // an effect twice in development - the second call would find the nonce gone.
  const presented = useRef(false);

  useEffect(() => {
    if (incomplete) return;
    if (presented.current) return;
    presented.current = true;

    // No unmount guard: the link is already spent by the time the answer arrives, so a
    // cancelled effect would leave somebody signed in on a page that never moves. React
    // remounts this once in development, which the ref above is what handles.
    void (async () => {
      try {
        const result = await consumeInloglink(nonce, token);

        if (result.status === "aangemeld") {
          // The session query is what every screen reads; without this the app would render
          // its signed-out state for one beat.
          await queryClient.invalidateQueries();
          // Back where they were when they asked, if that is where the link came from.
          await navigate({ to: result.returnUrl ?? "/", replace: true });
          return;
        }

        setStatus(result.status);
      } catch {
        setStatus("ongeldig");
      }
    })();
  }, [incomplete, navigate, nonce, queryClient, token]);

  if (status === "bezig") {
    return (
      <Shell>
        <Card className="mx-auto max-w-lg">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">{t("inloglink.landing.busy")}</p>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  return (
    <Shell>
      <InloglinkResultaat status={status} />
    </Shell>
  );
}

function InloglinkResultaat({ status }: { status: UseInloglinkStatus }) {
  const { t } = useTranslation(["session", "common"]);

  return (
    <Card className="mx-auto max-w-lg">
      <CardHeader>
        <CardTitle>
          {status === "geen-persoon"
            ? t("inloglink.landing.unknown.title")
            : t("inloglink.landing.invalid.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {status === "geen-persoon" ? (
          <p>{t("inloglink.landing.unknown.body", { mailbox: t("common:mailbox") })}</p>
        ) : (
          <p>{t("inloglink.landing.invalid.body")}</p>
        )}
        <p>
          <a className="underline" href="/">
            {t("inloglink.landing.back")}
          </a>
        </p>
      </CardContent>
    </Card>
  );
}
